import { BaseWagesApiPayload, PositionWagesApiPayload } from '@sdk/labor/types/apiTypes';
import getNowDate from '@sdk/utils/getNowDate';
import formatDate from '@sdk/utils/dateFormat/formatDate';
import gettext from '@sdk/lang/gettext';

import type {
  RestangularEmployee,
  Employee,
  RestangularPosition,
  Position,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';

export const mapEmployeesFromRestangularToEmployeeOnboardingModel = (
  employees: RestangularEmployee[]
): Employee[] =>
  employees.map((employee) => ({
    id: employee.id,
    fullName: employee.fullname,
    wage: employee.wages.base?.[0]?.regularRate?.toString(),
    wageId: employee.wages.base?.[0]?.id,
  }));

export const emptyEmployeeFactory = () => ({
  id: 0,
  fullName: undefined,
  wage: undefined,
});

/**
 * Parses fields typed in Define Wages step and make wages Payload from them.
 */
export const getWagesPayloadFromEmployeesFields = (
  employees: Employee[]
): BaseWagesApiPayload[] => {
  return employees
    .filter((employee) => Boolean(employee.wage))
    .map((employee) => {
      const today = formatDate(getNowDate());
      const todayWithTimezone = `${today}T00:00.000Z`;

      const wage: BaseWagesApiPayload = {
        dateEffective: today,
        fromDate: todayWithTimezone,
        regularRate: employee.wage,
        isSalary: false,
        locationId: null,
        userId: employee.id,
      };

      if (employee.wageId) {
        wage.id = employee.wageId;
      }

      return wage;
    });
};

export const mapPositionsFromRestangularToPositionOnboardingModel = (
  positions: RestangularPosition[]
): Position[] =>
  positions.map((position) => ({
    id: position.id,
    name: position.name,
    color: position.color,
    wage: position.wages?.[0]?.regularRate?.toString(),
    wageId: position.wages?.[0]?.id,
  }));

export const emptyPositionFactory = () => ({
  id: 0,
  name: undefined,
  wage: undefined,
  color: undefined,
});

/**
 * Parses fields typed in Define Wages step and make wages Payload from them.
 */
export const getWagesPayloadFromPositionsFields = (
  positions: Position[]
): PositionWagesApiPayload[] =>
  positions
    .filter((position) => Boolean(position.wage))
    .map((position) => {
      const today = formatDate(getNowDate());
      const todayWithTimezone = `${today}T00:00.000Z`;

      const wage: PositionWagesApiPayload = {
        dateEffective: today,
        fromDate: todayWithTimezone,
        regularRate: position.wage,
        positionId: position.id,
      };

      if (position.wageId) {
        wage.id = position.wageId;
      }

      return wage;
    });

/**
 * Function returns string with error description in case of error and nothing if everything is all right.
 */
export const getWageError = (wage?: string) => {
  if (!wage) {
    // Field is not required so undefined or empty string means it hasn't been specified, therefore no error
    return;
  }

  const wageValue = Number(wage);

  if (Number.isNaN(wageValue)) {
    return gettext('Provide a valid numeric wage value');
  }

  if (wageValue <= 0) {
    return gettext('Wage has to be greater than 0');
  }
};

/**
 * Helper to judge if field really changed it value (suitable for shopify/react-forms fields).
 */
export const isFieldDirty = (field) => {
  // If field was empty, then modified, then made empty again - that's no dirty.
  if (field.value === '' && typeof field.defaultValue === 'undefined') {
    return false;
  }
  return field.dirty;
};
